type Beacon = ((method: string, options?: unknown, data?: unknown) => unknown) & {
  readyQueue?: { method: string; options?: unknown; data?: unknown }[]
}

declare global {
  interface Window {
    Beacon?: Beacon
  }
}

const BEACON_SCRIPT = 'https://beacon-v2.helpscout.net'
export const BEACON_LOAD_TIMEOUT_MS = 8000

let ready: Promise<Beacon | null> | undefined

const installStub = (beaconId: string) => {
  const stub: Beacon = (method, options, data) => {
    stub.readyQueue?.push({ method, options, data })
  }
  stub.readyQueue = []
  window.Beacon = stub
  stub('init', beaconId)
  stub('config', { display: { style: 'manual' } })
}

const injectScript = (beaconId: string) =>
  new Promise<Beacon | null>((resolve) => {
    installStub(beaconId)
    const script = document.createElement('script')
    script.async = true
    script.src = BEACON_SCRIPT
    script.onload = () => resolve(window.Beacon ?? null)
    script.onerror = () => {
      script.remove()
      delete window.Beacon
      ready = undefined
      resolve(null)
    }
    document.head.appendChild(script)
  })

// A slow load only gives up for this call; the one script keeps loading for the next.
export const loadBeacon = (beaconId: string): Promise<Beacon | null> => {
  ready ??= injectScript(beaconId)
  const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), BEACON_LOAD_TIMEOUT_MS))
  return Promise.race([ready, timeout])
}

export const isBeaconOpen = () => {
  const info = window.Beacon?.('info') as { status?: { isOpened?: boolean } } | undefined
  return Boolean(info?.status?.isOpened)
}

export const closeBeacon = () => window.Beacon?.('close')

export const logoutBeacon = () => window.Beacon?.('logout', { endActiveChat: true })
